import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { authService } from '../../services/auth';
import type { AuditEntry } from '../../services/types';

const ACTION_COLORS: Record<string, string> = {
  login: 'text-emerald-400 bg-emerald-500/10',
  login_failed: 'text-red-400 bg-red-500/10',
  logout: 'text-slate-400 bg-slate-500/10',
  user_created: 'text-indigo-400 bg-indigo-500/10',
  user_updated: 'text-amber-400 bg-amber-500/10',
  user_deleted: 'text-red-400 bg-red-500/10',
  password_changed: 'text-amber-400 bg-amber-500/10',
  password_migrated: 'text-cyan-400 bg-cyan-500/10',
  refresh_token_reuse_detected: 'text-red-400 bg-red-500/10',
};

export function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const fetchAudit = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const res = await authService.apiFetch(
        `/api/auth/audit?limit=${limit}&offset=${newOffset}`,
      );
      const data = await res.json();
      if (data.success) {
        setEntries(data.data.entries);
        setTotal(data.data.total);
        setOffset(newOffset);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Аудит</h1>
          <p className="text-sm text-slate-500 mt-1">
            Всего записей: {total}
          </p>
        </div>
        <button
          onClick={() => fetchAudit(0)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-white/[0.06] overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="bg-slate-800/60 text-slate-400">
                  <th className="text-left px-4 py-3 font-medium">Время</th>
                  <th className="text-left px-4 py-3 font-medium">Действие</th>
                  <th className="text-left px-4 py-3 font-medium">Пользователь</th>
                  <th className="text-left px-4 py-3 font-medium">Детали</th>
                  <th className="text-left px-4 py-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-t border-white/[0.04] hover:bg-white/[0.02] transition"
                  >
                    <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          ACTION_COLORS[entry.action] || 'text-slate-400 bg-slate-700/50'
                        }`}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-300 text-xs">
                      {entry.user_id}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs max-w-[200px] truncate">
                      {entry.details || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs font-mono">
                      {entry.ip || '—'}
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-600">
                      Нет записей
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                disabled={currentPage <= 1}
                onClick={() => fetchAudit(offset - limit)}
                className="px-3 py-1.5 rounded-lg text-sm text-slate-400 bg-slate-800/60 border border-white/[0.06] hover:bg-slate-700 transition disabled:opacity-30"
              >
                ←
              </button>
              <span className="text-sm text-slate-500">
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => fetchAudit(offset + limit)}
                className="px-3 py-1.5 rounded-lg text-sm text-slate-400 bg-slate-800/60 border border-white/[0.06] hover:bg-slate-700 transition disabled:opacity-30"
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
